import React, { useEffect, useRef } from "react";

import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
// import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import NewspaperOutlinedIcon from "@mui/icons-material/NewspaperOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TuneIcon from "@mui/icons-material/Tune";
import { Box, Button, CircularProgress, Menu, MenuItem, Stack, Typography } from "@mui/material";

import { ArticleList } from "../../components/News-feed/components/ArticleList";
import { BriefingCard } from "../../components/News-feed/components/BriefingCard";
// import { RoleComponentGuard } from "components";

import { DigestList } from "../../components/News-feed/components/DigestList";
import { NewsFeedFilters } from "../../components/News-feed/components/NewsFeedFilters";
import { Search } from "../../components/News-feed/components/Search";
import { FeedTab, NewsFeedProvider, useNewsFeed } from "../../components/News-feed/context/NewsFeedContext";

// import { RoleName } from "../../constants";

export const NewsFeed: React.FC = () => (
  <NewsFeedProvider>
    <NewsFeedPageContent />
  </NewsFeedProvider>
);

const NewsFeedPageContent: React.FC = () => {
  const {
    articles,
    articlesHasMore,
    articlesLoading,
    loadMoreArticles,
    digestHistory,
    briefing,
    feedTab,
    setFeedTab,
    openConfigureFeed,
    // openDealCoverageDialog,
    // openCoverageManager,
    openDigestSettings,
    openBriefing,
    openDigestHighlights
  } = useNewsFeed();

  const [settingsAnchor, setSettingsAnchor] = React.useState<null | HTMLElement>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (feedTab !== FeedTab.LiveFeed) return;
    if (!articlesHasMore) return;
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreArticles();
      },
      { root, rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [feedTab, articlesHasMore, loadMoreArticles, articles.length]);

  return (
    <Stack
      direction="column"
      gap="16px"
      sx={{
        flex: 1,
        overflow: "hidden"
      }}
    >
      <Stack sx={{ px: "20px", flexShrink: 0 }} gap="16px">
        <Typography sx={{ fontSize: "24px", fontWeight: 500, lineHeight: "32px", color: "#071F35" }}>
          News Feed
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: "24px" }}>
          <Stack direction="row" alignItems="stretch" gap="16px">
            <Button
              type="button"
              disableRipple
              onClick={() => setFeedTab(FeedTab.LiveFeed)}
              startIcon={<NewspaperOutlinedIcon sx={{ fontSize: "18px !important" }} />}
              sx={{
                py: "2px",
                px: 0,
                pb: "6px",
                gap: "8px",
                borderBottom: "2px solid",
                borderColor: feedTab === FeedTab.LiveFeed ? "primary.main" : "transparent",
                color: feedTab === FeedTab.LiveFeed ? "primary.main" : "secondary.main",
                fontSize: 14,
                lineHeight: "16px",
                fontWeight: 500,
                borderRadius: 0,
                textTransform: "none",
                bgcolor: "transparent",
                minWidth: 0,
                whiteSpace: "nowrap",
                "& .MuiButton-startIcon": { m: 0 },
                "&:hover": {
                  bgcolor: "transparent",
                  color: "primary.main"
                }
              }}
            >
              Live Feed
            </Button>
            <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "#DBE8F5", mb: "6px" }} />
            <Button
              type="button"
              disableRipple
              onClick={() => setFeedTab(FeedTab.PastDigests)}
              startIcon={<ArchiveOutlinedIcon sx={{ fontSize: "18px !important" }} />}
              sx={{
                py: "2px",
                px: 0,
                pb: "6px",
                gap: "8px",
                borderBottom: "2px solid",
                borderColor: feedTab === FeedTab.PastDigests ? "primary.main" : "transparent",
                color: feedTab === FeedTab.PastDigests ? "primary.main" : "secondary.main",
                fontSize: 14,
                lineHeight: "16px",
                fontWeight: 500,
                borderRadius: 0,
                textTransform: "none",
                bgcolor: "transparent",
                minWidth: 0,
                whiteSpace: "nowrap",
                "& .MuiButton-startIcon": { m: 0 },
                "&:hover": {
                  bgcolor: "transparent",
                  color: "primary.main"
                }
              }}
            >
              Past Digests
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {feedTab === FeedTab.LiveFeed ? (
        <Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="20px"
            sx={{
              py: "10px",
              px: "16px",
              bgcolor: "#fff",
              borderRadius: "8px",
              boxShadow: "0px 1px 1px rgba(18, 18, 23, 0.1)",
              marginRight: "15px"
            }}
          >
            <Stack direction="row" alignItems="center" gap="10px" sx={{ flex: 1, minWidth: 0 }}>
              <NewsFeedFilters />
            </Stack>

            <Stack direction="row" alignItems="center" gap="10px" sx={{ flexShrink: 0 }}>
              <Search />

              <Button
                variant="outlined"
                disableElevation
                startIcon={<TuneIcon sx={{ fontSize: 16, color: "#071F35" }} />}
                onClick={(e) => setSettingsAnchor(e.currentTarget)}
                sx={{
                  height: 26,
                  minHeight: 26,
                  borderRadius: "32px",
                  px: "8px",
                  py: "4px",
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "16px",
                  textTransform: "none",
                  color: "#071F35",
                  bgcolor: "#fff",
                  border: "1px solid #7B92A9",
                  boxShadow: "none",
                  minWidth: 0,
                  gap: "4px",
                  "& .MuiButton-startIcon": { mr: 0, ml: 0 },
                  "&:hover": {
                    bgcolor: "#F6F8FC",
                    border: "1px solid #7B92A9",
                    boxShadow: "none"
                  }
                }}
              >
                Feed settings
              </Button>
            </Stack>

            <Menu anchorEl={settingsAnchor} open={Boolean(settingsAnchor)} onClose={() => setSettingsAnchor(null)}>
              <MenuItem
                onClick={() => {
                  setSettingsAnchor(null);
                  openConfigureFeed();
                }}
              >
                <TuneIcon fontSize="sm" sx={{ mr: 1 }} /> Feed configuration
              </MenuItem>
              {/* <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
              <MenuItem
                onClick={() => {
                  setSettingsAnchor(null);
                  openCoverageManager();
                }}
              >
                <ManageSearchIcon fontSize="sm" sx={{ mr: 1 }} /> Deal coverage
              </MenuItem>
            </RoleComponentGuard> */}
              <MenuItem
                onClick={() => {
                  setSettingsAnchor(null);
                  openDigestSettings();
                }}
              >
                <ScheduleIcon fontSize="sm" sx={{ mr: 1 }} /> Digest settings
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>
      ) : null}

      <Stack
        ref={scrollRef}
        direction="column"
        sx={{ flex: 1, overflow: "auto", bgcolor: "#f6f8fc", py: 1, paddingRight: "15px" }}
      >
        {feedTab === FeedTab.LiveFeed ? (
          <>
            <BriefingCard briefing={briefing} onOpenFull={openBriefing} />
            {articles.length ? (
              <>
                <ArticleList
                  articles={articles}
                  // onDealCoverage={openDealCoverageDialog}
                />
                {articlesHasMore && <Box ref={sentinelRef} sx={{ height: 1 }} />}
                {articlesLoading && (
                  <Stack alignItems="center" sx={{ py: 2 }}>
                    <CircularProgress size={24} />
                  </Stack>
                )}
              </>
            ) : articlesLoading ? (
              <Stack alignItems="center" sx={{ py: 6 }}>
                <CircularProgress size={28} />
              </Stack>
            ) : (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <DescriptionIcon sx={{ width: "22px", height: "22px", fontSize: 48, color: "#797979", mb: 1 }} />
                <Typography variant="h6">No news available right now</Typography>
                <Typography variant="body2" color="text.secondary">
                  Topics are set, but no articles passed the relevance filter for this cycle. Try adjusting your topic
                  configuration.
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <DigestList rows={digestHistory} onOpenHighlights={openDigestHighlights} />
        )}
      </Stack>
    </Stack>
  );
};
